/**
 * Sawyer 08 meds-feeding (A1/A2/A3/G10) — WHEN a dog's meals and medications
 * are due. The one implementation every surface uses: the Meds & feeding
 * report, the med pass, the Day list line and the printed run card. Two
 * surfaces can never place a dose differently because there is only this
 * file to ask (DESIGN-PHILOSOPHY #1, refuter C-6).
 *
 * FUNCTIONAL CORE: pure functions over plain data. No Prisma, no Express, no
 * clock (the caller passes the day and the zone), so the admin app imports it
 * straight into the browser and every rule is a unit test.
 *
 * The time-of-day words are CareEvent's own medication slots
 * (`MEDICATION_SLOTS`: am / midday / pm / bedtime / as_needed), shown as
 * AM / Noon / PM / Bedtime / As needed (PRN). Meals are breakfast / lunch /
 * dinner and sit in AM / Noon / PM.
 */
import { match } from 'ts-pattern';
import {
  MEDICATION_SLOTS,
  localDayOf,
  type CareEventRecord,
  type MedicationSlot,
} from './service.js';

// ──────────────────────────────────────────────────────────────────────
// Value sets and labels
// ──────────────────────────────────────────────────────────────────────

/** Report / med-pass group order. Always all five, always this order. */
export const REPORT_SLOTS: readonly MedicationSlot[] = MEDICATION_SLOTS;

export const SLOT_LABELS: Readonly<Record<MedicationSlot, string>> = {
  am: 'AM',
  midday: 'Noon',
  pm: 'PM',
  bedtime: 'Bedtime',
  as_needed: 'As needed (PRN)',
};

/** Meals staff can schedule. `snack` stays a log-only CareEvent slot. */
export const SCHEDULED_MEALS = ['breakfast', 'lunch', 'dinner'] as const;
export type ScheduledMeal = (typeof SCHEDULED_MEALS)[number];

export const MEAL_LABELS: Readonly<Record<ScheduledMeal, string>> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
};

export const MEAL_TO_SLOT: Readonly<Record<ScheduledMeal, MedicationSlot>> = {
  breakfast: 'am',
  lunch: 'midday',
  dinner: 'pm',
};

/** Boarders with no meal times on file eat at the run card's AM / PM feeds. */
export const HOUSE_DEFAULT_BOARDING_MEALS: readonly ScheduledMeal[] = ['breakfast', 'dinner'];

export type CareService = 'boarding' | 'daycare';

// ──────────────────────────────────────────────────────────────────────
// Medication times
// ──────────────────────────────────────────────────────────────────────

/**
 * Where a medication's times came from:
 *   set               — staff chose the times (`medications.slots`)
 *   frequency         — read from a recognisable frequency ("twice daily",
 *                       "as needed", "daily", "evening"); placed, labelled
 *                       "(from frequency — set times to confirm)"
 *   frequency_default — a frequency that names no time ("weekly", "monthly",
 *                       anything else typed). The paper run card has always
 *                       printed these as one AM box, so it still does; the
 *                       report does NOT pretend to know and lists it under
 *                       "Times not set" with "(run card shows AM)"
 *   unscheduled       — nothing at all to go on (no times, no frequency)
 */
export type SlotSource = 'set' | 'frequency' | 'frequency_default' | 'unscheduled';

export interface EffectiveSlots {
  slots: MedicationSlot[];
  source: SlotSource;
}

/** Keep only known slots, once each, in report order. NULL-safe. */
export function normalizeSlots(raw: readonly string[] | null | undefined): MedicationSlot[] {
  if (!raw || raw.length === 0) return [];
  const set = new Set(raw);
  return REPORT_SLOTS.filter((s) => set.has(s));
}

/** Keep only known meals, once each, in day order. NULL-safe. */
export function normalizeMeals(raw: readonly string[] | null | undefined): ScheduledMeal[] {
  if (!raw || raw.length === 0) return [];
  const set = new Set(raw);
  return SCHEDULED_MEALS.filter((m) => set.has(m));
}

/**
 * The times a FREQUENCY string implies. This is the run card's historical
 * parser (run-card-grid.ts `periodsForFrequency`), moved here so it is the
 * only one; its branch order is unchanged so the paper card prints exactly
 * what it printed before.
 */
export function frequencySlots(frequency: string | null | undefined): EffectiveSlots {
  const f = (frequency ?? '').toLowerCase().trim();
  if (!f) return { slots: [], source: 'unscheduled' };
  if (/twice|2x|bid|two times|morning.*(even|night)|am.*pm/.test(f)) {
    return { slots: ['am', 'pm'], source: 'frequency' };
  }
  if (/(as[\s_-]?needed|prn|as[\s_-]?required)/.test(f)) {
    return { slots: ['as_needed'], source: 'frequency' };
  }
  if (/(even|night|bedtime|pm\b|qpm)/.test(f)) return { slots: ['pm'], source: 'frequency' };
  // daily / once / every morning / qd / qam name a single morning dose.
  if (/(daily|once|morning|\bqd\b|qam|\bam\b|every day|1x)/.test(f) && !/(week|month)/.test(f)) {
    return { slots: ['am'], source: 'frequency' };
  }
  // weekly / monthly / free text: the card's single AM box, flagged.
  return { slots: ['am'], source: 'frequency_default' };
}

/** A medication's effective times: what staff set, else its frequency. */
export function effectiveMedicationSlots(med: {
  slots?: readonly string[] | null;
  frequency?: string | null;
}): EffectiveSlots {
  const set = normalizeSlots(med.slots);
  if (set.length > 0) return { slots: set, source: 'set' };
  return frequencySlots(med.frequency ?? null);
}

/** True when the report may place the medication into time groups. */
export function isPlaceable(source: SlotSource): boolean {
  return match(source)
    .with('set', 'frequency', () => true)
    .with('frequency_default', 'unscheduled', () => false)
    .exhaustive();
}

/** The label the paper run card prints for a slot. */
export const RUN_CARD_SLOT_LABEL: Readonly<Record<MedicationSlot, string>> = {
  am: 'AM',
  midday: 'Noon',
  pm: 'PM',
  bedtime: 'Bedtime',
  as_needed: 'PRN',
};

/**
 * Run-card periods for one medication. `''` = one unlabelled row (a med with
 * no time information at all is never dropped from the paper ledger).
 */
export function runCardPeriods(med: {
  slots?: readonly string[] | null;
  frequency?: string | null;
}): string[] {
  const eff = effectiveMedicationSlots(med);
  if (eff.slots.length === 0) return [''];
  return eff.slots.map((s) => RUN_CARD_SLOT_LABEL[s]);
}

// ──────────────────────────────────────────────────────────────────────
// Meal times and the lunch marker (#1219)
// ──────────────────────────────────────────────────────────────────────

/**
 * Where a meal came from:
 *   set           — the dog's meal times, or this visit's lunch override
 *   house_default — a boarder with no meal times on file (breakfast + dinner)
 *   meal_add_on   — a daycare visit carrying the paid House Food add-on
 *                   (`grooming_add_ons.is_meal_charge`), so the board's lunch
 *                   rule (lane 03) is a subset of this one (refuter C-4)
 */
export type MealSource = 'set' | 'house_default' | 'meal_add_on';

export interface EffectiveMeals {
  meals: ScheduledMeal[];
  /** Per meal, why it is there. */
  sources: Partial<Record<ScheduledMeal, MealSource>>;
  /** Does this dog eat lunch here on this visit? */
  eatsLunch: boolean;
}

/**
 * The meals a dog gets on one visit.
 *   boarding: its meal times, or the house default (breakfast + dinner);
 *             the visit's lunch override can add or remove lunch.
 *   daycare:  lunch only, and only if the visit says so (override), else the
 *             dog's standing marker (meal times include lunch), else a paid
 *             meal add-on on the visit. Breakfast and dinner happen at home.
 * The add-on is never REQUIRED (Sawyer's lunches are a feeding instruction);
 * it only counts when nothing else decides.
 */
export function effectiveFeedingSlots(args: {
  feedingSlots: readonly string[] | null | undefined;
  service: CareService;
  lunchOverride: boolean | null | undefined;
  hasMealChargeLeg?: boolean;
}): EffectiveMeals {
  const standing = normalizeMeals(args.feedingSlots);
  const override = args.lunchOverride ?? null;
  return match(args.service)
    .with('boarding', (): EffectiveMeals => {
      const base = standing.length > 0 ? standing : [...HOUSE_DEFAULT_BOARDING_MEALS];
      const baseSource: MealSource = standing.length > 0 ? 'set' : 'house_default';
      const sources: Partial<Record<ScheduledMeal, MealSource>> = {};
      for (const m of base) sources[m] = baseSource;
      if (override === true) sources.lunch = 'set';
      if (override === false) delete sources.lunch;
      const meals = SCHEDULED_MEALS.filter((m) => sources[m] !== undefined);
      return { meals, sources, eatsLunch: meals.includes('lunch') };
    })
    .with('daycare', (): EffectiveMeals => {
      let source: MealSource | null = null;
      if (override !== null) source = override ? 'set' : null;
      else if (standing.includes('lunch')) source = 'set';
      else if (args.hasMealChargeLeg) source = 'meal_add_on';
      return source
        ? { meals: ['lunch'], sources: { lunch: source }, eatsLunch: true }
        : { meals: [], sources: {}, eatsLunch: false };
    })
    .exhaustive();
}

/**
 * Which groups a service is listed in. Daycare dogs are here midday only:
 * their AM / PM / bedtime care happens at home (an HTHD default nobody has
 * confirmed yet; see the PR's open questions).
 */
export function slotsServedFor(service: CareService): readonly MedicationSlot[] {
  return match(service)
    .with('boarding', () => REPORT_SLOTS)
    .with('daycare', (): readonly MedicationSlot[] => ['midday', 'as_needed'])
    .exhaustive();
}

/**
 * Where one active medication lands for one visit — the ONE classifier the
 * report, the printout, the med pass and the Day list row all read, so no
 * two surfaces can describe the same medication differently (review round
 * 1: the report called a daycare dog's AM/PM medication "times not set"
 * while the Day list correctly left it to home).
 *
 *   scheduled     — at least one of its times is served on this visit;
 *                   `slots` are those served times.
 *   at_home       — its times are known (set, or read from the frequency)
 *                   but none is served on this visit: a daycare dog's
 *                   AM / PM / bedtime dose is given at home. `slots` are
 *                   the known times, for the "Given at home (AM, PM)" line.
 *   times_not_set — no usable time: unscheduled, or only the run card's
 *                   flagged AM default. The one case "Set times" can fix.
 */
export type MedicationPlacement =
  | { kind: 'scheduled'; slots: MedicationSlot[] }
  | { kind: 'at_home'; slots: MedicationSlot[] }
  | { kind: 'times_not_set' };

export function placeMedicationFor(
  service: CareService,
  med: { slots?: readonly string[] | null; frequency?: string | null },
): MedicationPlacement {
  const eff = effectiveMedicationSlots(med);
  if (!isPlaceable(eff.source) || eff.slots.length === 0) return { kind: 'times_not_set' };
  const served = new Set(slotsServedFor(service));
  const here = eff.slots.filter((s) => served.has(s));
  return here.length > 0 ? { kind: 'scheduled', slots: here } : { kind: 'at_home', slots: [...eff.slots] };
}

// ──────────────────────────────────────────────────────────────────────
// Dose status (append-only: the latest entry that day is the status)
// ──────────────────────────────────────────────────────────────────────

export interface DoseHistoryEntry {
  id: string;
  outcome: string;
  staffName: string | null;
  occurredAt: string;
  notes: string | null;
}

export interface DoseStatus {
  latest: { outcome: string; staffName: string | null; occurredAt: string } | null;
  /** Every entry that local day, newest first. */
  history: DoseHistoryEntry[];
  /** How many entries that day (the PRN "Given 2× today"). */
  countToday: number;
}

export function staffDisplayName(e: Pick<CareEventRecord, 'staffFirstName' | 'staffLastName'>): string | null {
  const first = (e.staffFirstName ?? '').trim();
  if (first) return first;
  const last = (e.staffLastName ?? '').trim();
  return last || null;
}

function newestFirst(a: CareEventRecord, b: CareEventRecord): number {
  const at = new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime();
  if (at !== 0) return at;
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

/**
 * The status of one dose (medication × slot) on one facility day. Later
 * `occurredAt` wins; a tie is broken by `createdAt` (the correction recorded
 * second). Every entry stays in `history`: a correction never erases.
 */
export function doseStatusForDay(
  events: readonly CareEventRecord[],
  opts: { medicationId: string; slot: string; day: string; timeZone: string },
): DoseStatus {
  const mine = events
    .filter(
      (e) =>
        e.kind === 'medication' &&
        e.medicationId === opts.medicationId &&
        e.slot === opts.slot &&
        localDayOf(new Date(e.occurredAt), opts.timeZone) === opts.day,
    )
    .slice()
    .sort(newestFirst);
  const history = mine.map((e) => ({
    id: e.id,
    outcome: e.outcome,
    staffName: staffDisplayName(e),
    occurredAt: e.occurredAt,
    notes: e.notes,
  }));
  const top = history[0];
  return {
    latest: top ? { outcome: top.outcome, staffName: top.staffName, occurredAt: top.occurredAt } : null,
    history,
    countToday: history.length,
  };
}

// ──────────────────────────────────────────────────────────────────────
// The staff medication view (profile, day read, run card)
// ──────────────────────────────────────────────────────────────────────

export interface StaffMedicationView {
  id: string;
  name: string;
  dosage: string | null;
  frequency: string | null;
  instructions: string | null;
  slots: MedicationSlot[];
  effectiveSlots: MedicationSlot[];
  slotSource: SlotSource;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
}

function isoDayOf(v: Date | string | null | undefined): string | null {
  if (v === null || v === undefined) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.toISOString().slice(0, 10);
  return v.length >= 10 ? v.slice(0, 10) : v;
}

export function toStaffMedicationView(m: {
  id: string;
  name: string;
  dosage: string | null;
  frequency: string | null;
  instructions: string | null;
  slots?: readonly string[] | null;
  startDate: Date | string | null;
  endDate: Date | string | null;
  isActive: boolean;
}): StaffMedicationView {
  const eff = effectiveMedicationSlots(m);
  return {
    id: m.id,
    name: m.name,
    dosage: m.dosage,
    frequency: m.frequency,
    instructions: m.instructions,
    slots: normalizeSlots(m.slots),
    effectiveSlots: eff.slots,
    slotSource: eff.source,
    startDate: isoDayOf(m.startDate),
    endDate: isoDayOf(m.endDate),
    isActive: m.isActive,
  };
}

// ──────────────────────────────────────────────────────────────────────
// Sawyer 08 meds-feeding (A3) — the Day list's one-line care summary.
// ──────────────────────────────────────────────────────────────────────

export const BOARDING_SERVICE_NAMES: readonly string[] = ['boarding'];
export const DAYCARE_SERVICE_NAMES: readonly string[] = ['daycare', 'daycare-half'];

/** 'boarding' | 'daycare' for a service-type name; anything else has no care line. */
export function careServiceOf(serviceName: string | null | undefined): CareService | null {
  const n = (serviceName ?? '').trim().toLowerCase();
  if (BOARDING_SERVICE_NAMES.includes(n)) return 'boarding';
  if (DAYCARE_SERVICE_NAMES.includes(n)) return 'daycare';
  return null;
}

export interface DayListCare {
  meals: ScheduledMeal[];
  mealSources: Partial<Record<ScheduledMeal, MealSource>>;
  eatsLunch: boolean;
  meds: Array<{
    name: string;
    /** The times served on this visit (empty unless placement is 'scheduled'). */
    slots: MedicationSlot[];
    slotSource: SlotSource;
    /** placeMedicationFor's answer — the field the row branches on. */
    placement: MedicationPlacement['kind'];
  }>;
  hasMedicationNotes: boolean;
  hasFeedingNotes: boolean;
}

/** What the Day list row says about one dog on one visit. Same rules as the report. */
export function dayListCareOf(args: {
  service: CareService;
  feedingSlots: readonly string[] | null | undefined;
  feedingNotes: string | null | undefined;
  lunchOverride: boolean | null | undefined;
  hasMealChargeLeg: boolean;
  medicationNotes: string | null | undefined;
  medications: ReadonlyArray<{ name: string; frequency?: string | null; slots?: readonly string[] | null }>;
}): DayListCare {
  const meals = effectiveFeedingSlots({
    feedingSlots: args.feedingSlots,
    service: args.service,
    lunchOverride: args.lunchOverride,
    hasMealChargeLeg: args.hasMealChargeLeg,
  });
  return {
    meals: meals.meals,
    mealSources: meals.sources,
    eatsLunch: meals.eatsLunch,
    meds: args.medications.map((m) => {
      const placement = placeMedicationFor(args.service, m);
      return {
        name: m.name,
        slots: placement.kind === 'scheduled' ? placement.slots : [],
        slotSource: effectiveMedicationSlots(m).source,
        placement: placement.kind,
      };
    }),
    hasMedicationNotes: Boolean(args.medicationNotes && args.medicationNotes.trim()),
    hasFeedingNotes: Boolean(args.feedingNotes && args.feedingNotes.trim()),
  };
}
